import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppPageShell from "../components/AppPageShell";
import AppSectionCard from "../components/AppSectionCard";
import { pageStyles } from "../styles/appTheme";
import LocalLibrarianPanel from "../components/LocalLibrarianPanel";


function normalizeStatusData(data: any) {
  if (!data) return data;

  // If coming from legacy admin_status route
  if (data.brain && data.brain.active_provider !== undefined) {
    return data;
  }

  // If coming from different structure, normalize
  return {
    ...data,
    brain: {
      active_provider: data?.brain?.active_provider || "none",
      fallback_provider: data?.brain?.fallback_provider || "local",
      preferred_provider: data?.brain?.preferred_provider || "avabrain",
      brain_api_online: !!data?.brain?.brain_api_online,
      openai_configured: !!data?.brain?.openai_configured,
      local_brain_enabled: !!data?.brain?.local_brain_enabled,
      brain_base_url: data?.brain?.brain_base_url || null
    }
  };
}


const API_BASE = import.meta.env.VITE_API_BASE || "";

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const safe = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|; )${safe}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function getStoredToken(): string {
  const keys = ["ava_session_token", "ava_token", "token", "access_token"];
  for (const key of keys) {
    const value =
      localStorage.getItem(key) ||
      sessionStorage.getItem(key) ||
      getCookie(key);
    if (value) return value;
  }
  return "";
}

function getStoredUser(): any {
  const keys = ["ava_user", "user", "profile", "ava_profile", "user_profile"];
  for (const key of keys) {
    try {
      const raw =
        localStorage.getItem(key) ||
        sessionStorage.getItem(key) ||
        getCookie(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    } catch {
      continue;
    }
  }
  return null;
}

type AdminStatus = {
  ok?: boolean;
  checked_at?: string;
  admin?: {
    email?: string;
    role?: string;
  };
  brain?: {
    brain_base_url?: string;
    brain_api_online?: boolean;
    brain_api_response?: any;
    openai_configured?: boolean;
    local_brain_enabled?: boolean;
    env_local_brain_enabled?: boolean;
    preferred_provider?: string;
    active_provider?: string;
    active_reason?: string;
    fallback_provider?: string;
  };
  services?: {
    api_online?: boolean;
    brain_online?: boolean;
    openai_ready?: boolean;
    local_brain_ready?: boolean;
  };
  providers?: Array<{
    name: string;
    configured: boolean;
    online: boolean;
    active: boolean;
    preferred?: boolean;
  }>;
  brain_controls?: {
    preferred_provider?: string;
    local_fallback_enabled?: boolean;
  };
};

type ElevationStatus = {
  ok?: boolean;
  eligible?: boolean;
  elevated?: boolean;
  elevated_until?: number | null;
  seconds_remaining?: number;
  message?: string;
};

export default function AdminConsole() {
  const navigate = useNavigate();
  const storedUser = useMemo(() => getStoredUser(), []);
  const token = getStoredToken();

  const headers: Record<string, string> = token
    ? {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    : {
        "Content-Type": "application/json",
      };

  const [status, setStatus] = useState<AdminStatus | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [userActivity, setUserActivity] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [statusWarning, setStatusWarning] = useState("");
  const [usersWarning, setUsersWarning] = useState("");
  const [invitesWarning, setInvitesWarning] = useState("");

  const [elevation, setElevation] = useState<ElevationStatus | null>(null);
  const [elevationPin, setElevationPin] = useState("");
  const [elevationModalOpen, setElevationModalOpen] = useState(false);
  const [elevationBusy, setElevationBusy] = useState(false);
  const [elevationMessage, setElevationMessage] = useState("");
  const [nowEpoch, setNowEpoch] = useState(
    () => Math.floor(Date.now() / 1000)
  );

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteDays, setInviteDays] = useState("5");
  const [selectedEmail, setSelectedEmail] = useState("");

  const [preferredProvider, setPreferredProvider] = useState("avabrain");
  const [localFallbackEnabled, setLocalFallbackEnabled] = useState(false);

  async function safeJson(res: Response) {
    return res.json().catch(() => ({}));
  }


  function responseDetail(data: any, fallback: string): string {
    if (typeof data?.detail === "string") return data.detail;
    if (typeof data?.message === "string") return data.message;
    return fallback;
  }

  async function loadElevationStatus() {
    try {
      const res = await fetch(
        `${API_BASE}/api/v1/chat/admin-elevation/status`,
        {
          method: "GET",
          credentials: "include",
          headers,
        }
      );

      const data = await safeJson(res);

      if (!res.ok) {
        setElevation({
          elevated: false,
          eligible: false,
          elevated_until: null,
        });

        setElevationMessage(
          responseDetail(
            data,
            "Unable to read administrator security status."
          )
        );
        return;
      }

      setElevation(data);

      if (data?.elevated) {
        await loadAll();
      } else {
        setStatus(null);
        setUsers([]);
        setInvites([]);
        setUserActivity(null);
        setLoading(false);
      }
    } catch (err: any) {
      setElevation({
        elevated: false,
        elevated_until: null,
      });

      setElevationMessage(
        err?.message ||
          "Administrator security status request failed."
      );
    }
  }

  async function submitElevationPin() {
    const pin = elevationPin.trim();

    if (!pin) {
      setElevationMessage("Enter the separate Admin PIN.");
      return;
    }

    try {
      setElevationBusy(true);
      setElevationMessage("");

      const res = await fetch(
        `${API_BASE}/api/v1/chat/verify-admin-pin`,
        {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify({
            pin,
          }),
        }
      );

      const data = await safeJson(res);

      if (!res.ok) {
        const retryAfter = res.headers.get("Retry-After");

        setElevationMessage(
          retryAfter
            ? `Too many attempts. Try again in ${retryAfter} seconds.`
            : responseDetail(
                data,
                "Administrator verification failed."
              )
        );

        setElevationPin("");
        await loadElevationStatus();
        return;
      }

      setElevationPin("");
      setElevationModalOpen(false);
      setElevationMessage(
        "Super Admin controls unlocked for ten minutes."
      );

      await loadElevationStatus();
    } catch (err: any) {
      setElevationMessage(
        err?.message ||
          "Administrator verification request failed."
      );
    } finally {
      setElevationBusy(false);
    }
  }

  async function lockElevation() {
    try {
      setElevationBusy(true);
      setElevationMessage("");

      const res = await fetch(
        `${API_BASE}/api/v1/chat/admin-elevation/logout`,
        {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify({}),
        }
      );

      const data = await safeJson(res);

      if (!res.ok) {
        setElevationMessage(
          responseDetail(
            data,
            "Unable to lock administrator controls."
          )
        );
        return;
      }

      sessionStorage.removeItem(
        "ava_admin_pin_verified"
      );

      setElevationPin("");
      setElevationModalOpen(false);
      setElevationMessage(
        "Administrator controls are locked."
      );

      await loadElevationStatus();
    } catch (err: any) {
      setElevationMessage(
        err?.message ||
          "Administrator lock request failed."
      );
    } finally {
      setElevationBusy(false);
    }
  }

  const loadAll = async () => {
    try {
      setLoading(true);
      setError("");
      setActionMsg("");
      setStatusWarning("");
      setUsersWarning("");
      setInvitesWarning("");

      const [statusRes, usersRes, invitesRes, activityRes] = await Promise.allSettled([
        fetch(`${API_BASE}/api/v1/admin/system-status-real`, {
          method: "GET",
          credentials: "include",
          headers,
        }),
        fetch(`${API_BASE}/api/v1/admin/users`, {
          method: "GET",
          credentials: "include",
          headers,
        }),
        fetch(`${API_BASE}/api/v1/admin/invites`, {
          method: "GET",
          credentials: "include",
          headers,
        }),
          fetch(`${API_BASE}/api/v1/admin/user-activity`, {
            method: "GET",
            credentials: "include",
            headers,
          }),
      ]);

      if (statusRes.status === "fulfilled") {
        const statusData = await safeJson(statusRes.value);
        if (statusRes.value.ok) {
          setStatus(normalizeStatusData(statusData));
          setPreferredProvider(statusData?.brain?.preferred_provider || "avabrain");
          setLocalFallbackEnabled(Boolean(statusData?.brain?.local_brain_enabled));
        } else {
          setStatusWarning(statusData?.detail || "System status endpoint returned an error.");
        }
      } else {
        setStatusWarning("System status request failed.");
      }

      if (usersRes.status === "fulfilled") {
        const usersData = await safeJson(usersRes.value);
        if (usersRes.value.ok) {
          setUsers(usersData?.users || []);
        } else {
          setUsersWarning(usersData?.detail || "Users endpoint returned an error.");
        }
      } else {
        setUsersWarning("Users request failed.");
      }

      if (invitesRes.status === "fulfilled") {
        const invitesData = await safeJson(invitesRes.value);
        if (invitesRes.value.ok) {
          setInvites(invitesData?.invites || []);
        } else {
          setInvitesWarning(invitesData?.detail || "Invites endpoint returned an error.");
        }
      } else {
        setInvitesWarning("Invites request failed.");
      }

        if (activityRes.status === "fulfilled") {
          const activityData = await safeJson(activityRes.value);

          if (activityRes.value.ok) {
            setUserActivity(activityData || null);
          } else {
            console.warn("User activity endpoint returned an error:", activityData?.detail || activityData);
            setUserActivity(null);
          }
        } else {
          console.warn("User activity request failed.");
          setUserActivity(null);
        }
    } catch (err: any) {
      setError(err?.message || "Admin console failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadElevationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowEpoch(
        Math.floor(Date.now() / 1000)
      );
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  async function postAction(path: string, body: any, method: string = "POST") {
    try {
      setActionMsg("");
      setError("");

      const res = await fetch(`${API_BASE}${path}`, {
        method,
        credentials: "include",
        headers,
        body: JSON.stringify(body),
      });

      const data = await safeJson(res);

      if (!res.ok) {
        const detail = responseDetail(
          data,
          "Request failed"
        );

        if (
          res.status === 403 &&
          (
            detail.includes("Administrator verification") ||
            detail.includes("Elevated Super Admin")
          )
        ) {
          setElevationMessage(
            "Unlock Super Admin controls to continue."
          );
          setElevationModalOpen(true);
          setActionMsg(
            "🔐 This operation requires the separate Admin PIN."
          );
          await loadElevationStatus();
          return;
        }

        setActionMsg(`❌ API Error: ${detail}`);
        return;
      }

      setActionMsg(data?.message || "✅ Action saved. Refreshing live data...");
      await loadAll();
    } catch (err: any) {
      setActionMsg(err?.message || "Action failed.");
    }
  }


  async function confirmUserAction(label: string, path: string, danger: boolean = false, method: string = "POST") {
    if (!selectedEmail) {
      setActionMsg("Select a user first.");
      return;
    }

    const ok = window.confirm(
      `${danger ? "⚠️ " : ""}${label}\n\nUser: ${selectedEmail}\n\nContinue?`
    );

    if (!ok) {
      setActionMsg("Action cancelled.");
      return;
    }

    await postAction(path, { email: selectedEmail }, method);
  }

  async function saveBrainControls() {
    await postAction("/api/v1/admin/brain-control", {
      preferred_provider: preferredProvider,
      local_fallback_enabled: localFallbackEnabled,
    });
  }

  async function confirmSelectedUserLive() {
    if (!selectedEmail) {
      setActionMsg("Select a user first.");
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/v1/admin/users`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(headers || {})
        }
      });

      const data = await safeJson(res);

      if (!res.ok) {
        setActionMsg(`❌ API Error: ${
          typeof data?.detail === "string"
            ? data.detail
            : JSON.stringify(data?.detail || data || "Request failed")
        }`);
        return;
      }
      const userList = Array.isArray(data) ? data : (data?.users || []);

      setUsers(userList);

      const liveUser = userList.find(
        (u: any) => String(u.email).toLowerCase() === String(selectedEmail).toLowerCase()
      );

      if (!liveUser) {
        setActionMsg(`Live check: ${selectedEmail} was not found.`);
        return;
      }

      setActionMsg(
        `Live confirmed: ${liveUser.email} • ${liveUser.role} • ${liveUser.status}`
      );

      await loadAll();
    } catch (err: any) {
      setActionMsg(err?.message || "Live confirmation failed.");
    }
  }

  const currentRole = String(storedUser?.role || "admin").toUpperCase();

  const elevationSecondsLeft = Math.max(
    0,
    Number(elevation?.elevated_until || 0) - nowEpoch
  );

  const isElevated = Boolean(
    elevation?.elevated &&
    elevationSecondsLeft > 0
  );

  useEffect(() => {
    if (isElevated) return;

    setStatus(null);
    setUsers([]);
    setInvites([]);
    setUserActivity(null);
    setSelectedEmail("");
    setLoading(false);
  }, [isElevated]);

  const elevationMinutes = Math.floor(
    elevationSecondsLeft / 60
  );

  const elevationSeconds = String(
    elevationSecondsLeft % 60
  ).padStart(2, "0");

  return (
    <AppPageShell
      kicker="Ava"
      title="Admin Console"
      subtitle="Manage users, access levels, Pro invitations, system health, and platform operations from one secure control surface."
      maxWidth="1320px"
    >
      <div style={styles.topRow}>
        <div style={styles.pill}>Current Access: {currentRole}</div>

        <button
          type="button"
          style={pageStyles.button}
          disabled={!isElevated || loading}
          onClick={() => void loadAll()}
        >
          {isElevated ? "Refresh" : "Admin Locked"}
        </button>


      </div>

      <div
        role="status"
        style={{
          marginBottom: "18px",
          padding: "16px",
          borderRadius: "16px",
          border: isElevated
            ? "1px solid rgba(110, 255, 170, 0.62)"
            : "1px solid rgba(76, 201, 255, 0.48)",
          background: isElevated
            ? "rgba(20, 84, 55, 0.26)"
            : "rgba(11, 42, 67, 0.52)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontWeight: 800,
              letterSpacing: "0.04em",
            }}
          >
            Super Admin Security: {isElevated ? "ELEVATED" : "LOCKED"}
          </div>

          <div
            style={{
              marginTop: "6px",
              opacity: 0.82,
              lineHeight: 1.5,
            }}
          >
            {isElevated
              ? `Sensitive controls remain unlocked for ${elevationMinutes}:${elevationSeconds}.`
              : elevation?.eligible === false
                ? "This account is not the designated Super Admin account."
                : "Sensitive controls require the separate Admin PIN."}
          </div>

          {elevationMessage ? (
            <div
              style={{
                marginTop: "8px",
                fontSize: "0.92rem",
              }}
            >
              {elevationMessage}
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          {isElevated ? (
            <button
              type="button"
              style={pageStyles.button}
              disabled={elevationBusy}
              onClick={() => void lockElevation()}
            >
              {elevationBusy ? "Locking..." : "Lock Admin"}
            </button>
          ) : (
            <button
              type="button"
              style={pageStyles.button}
              disabled={
                elevationBusy ||
                elevation?.eligible === false
              }
              onClick={() => {
                setElevationMessage("");
                setElevationModalOpen(true);
              }}
            >
              Unlock Admin
            </button>
          )}

          <button
            type="button"
            style={pageStyles.button}
            disabled={elevationBusy}
            onClick={() => void loadElevationStatus()}
          >
            Check Security Status
          </button>
        </div>
      </div>

      {elevationModalOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-elevation-title"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "rgba(0, 7, 16, 0.82)",
            backdropFilter: "blur(10px)",
            display: "grid",
            placeItems: "center",
            padding: "20px",
          }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setElevationModalOpen(false);
              setElevationPin("");
            }
          }}
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void submitElevationPin();
            }}
            style={{
              width: "min(440px, 100%)",
              borderRadius: "20px",
              border: "1px solid rgba(76, 201, 255, 0.58)",
              background: "rgba(7, 20, 35, 0.98)",
              boxShadow: "0 0 42px rgba(30, 168, 255, 0.24)",
              padding: "24px",
            }}
          >
            <h2
              id="admin-elevation-title"
              style={{
                margin: 0,
                fontSize: "1.35rem",
              }}
            >
              Unlock Super Admin Controls
            </h2>

            <p
              style={{
                margin: "10px 0 18px",
                opacity: 0.78,
                lineHeight: 1.55,
              }}
            >
              Enter the separate Admin PIN. The elevated session
              automatically expires after ten minutes.
            </p>

            <label
              htmlFor="admin-elevation-pin"
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: 700,
              }}
            >
              Admin PIN
            </label>

            <input
              id="admin-elevation-pin"
              type="password"
              autoFocus
              autoComplete="off"
              value={elevationPin}
              onChange={(event) =>
                setElevationPin(event.target.value)
              }
              style={{
                width: "100%",
                boxSizing: "border-box",
                borderRadius: "12px",
                border: "1px solid rgba(76, 201, 255, 0.56)",
                background: "rgba(0, 8, 18, 0.86)",
                color: "white",
                padding: "13px 14px",
                fontSize: "1rem",
                outline: "none",
              }}
            />

            {elevationMessage ? (
              <div
                style={{
                  marginTop: "12px",
                  lineHeight: 1.45,
                }}
              >
                {elevationMessage}
              </div>
            ) : null}

            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                style={pageStyles.button}
                disabled={elevationBusy}
                onClick={() => {
                  setElevationModalOpen(false);
                  setElevationPin("");
                }}
              >
                Cancel
              </button>

              <button
                type="submit"
                style={pageStyles.button}
                disabled={
                  elevationBusy ||
                  !elevationPin.trim()
                }
              >
                {elevationBusy ? "Verifying..." : "Unlock"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <div
        style={{
          position: "relative",
          minHeight: "420px",
        }}
      >
        <div
          inert={!isElevated}
          aria-hidden={!isElevated}
          style={{
            filter: isElevated
              ? "none"
              : "blur(15px)",
            opacity: isElevated
              ? 1
              : 0.2,
            pointerEvents: isElevated
              ? "auto"
              : "none",
            userSelect: isElevated
              ? "auto"
              : "none",
            transform: isElevated
              ? "none"
              : "scale(0.992)",
            transition:
              "filter 240ms ease, opacity 240ms ease, transform 240ms ease",
          }}
        >
      {loading ? <div style={styles.notice}>Loading admin console...</div> : null}
      {error ? <div style={styles.error}>{error}</div> : null}
      {actionMsg ? <div style={styles.notice}>{actionMsg}</div> : null}

      <div style={styles.grid}>
        <AppSectionCard>
          <div style={styles.sectionTitle}>Brain Status</div>

          <div style={styles.badgeRow}>
            <div style={styles.bigBadge}>
              ACTIVE: {status?.brain?.active_provider || "UNKNOWN"}
            </div>
            <div style={styles.smallBadge}>
              FALLBACK: {status?.brain?.fallback_provider || "NONE"}
            </div>
          </div>

          <div style={styles.statRow}>
            <div style={styles.statLabel}>Preferred Provider</div>
            <div style={styles.statValue}>
              {String(status?.brain?.preferred_provider || "—").toUpperCase()}
            </div>
          </div>

          <div style={styles.statRow}>
            <div style={styles.statLabel}>Brain API</div>
            <div style={styles.statValue}>
              {status?.brain?.brain_api_online ? "ONLINE" : "OFFLINE"}
            </div>
          </div>

          <div style={styles.statRow}>
            <div style={styles.statLabel}>OpenAI</div>
            <div style={styles.statValue}>
              {status?.brain?.openai_configured ? "CONFIGURED" : "NOT CONFIGURED"}
            </div>
          </div>

          <div style={styles.statRow}>
            <div style={styles.statLabel}>Local Fallback Enabled</div>
            <div style={styles.statValue}>
              {status?.brain?.local_brain_enabled ? "YES" : "NO"}
            </div>
          </div>

          <div style={styles.statRow}>
            <div style={styles.statLabel}>Brain Endpoint</div>
            <div style={styles.statValue}>
              {status?.brain?.brain_base_url || "—"}
            </div>
          </div>

          <div style={styles.note}>
            {status?.brain?.active_reason || "No brain details available."}
          </div>

          {statusWarning ? <div style={styles.warnBox}>{statusWarning}</div> : null}
        </AppSectionCard>

        <AppSectionCard>
          <div style={styles.sectionTitle}>Brain Switcher</div>

          <label style={styles.inputLabel}>Preferred Provider</label>
          <select
            value={preferredProvider}
            onChange={(e) => setPreferredProvider(e.target.value)}
            style={styles.select}
          >
            <option value="avabrain">AvaBrain API</option>
            <option value="openai">OpenAI</option>
            <option value="local">Local Brain</option>
          </select>

          <label style={{ ...styles.inputLabel, marginTop: "14px" }}>
            <input
              type="checkbox"
              checked={localFallbackEnabled}
              onChange={(e) => setLocalFallbackEnabled(e.target.checked)}
              style={{ marginRight: "8px" }}
            />
            Enable Local Fallback
          </label>

          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              marginTop: "16px",
            }}
          >
            <button type="button" style={pageStyles.button} onClick={saveBrainControls}>
              Save Brain Controls
            </button>
          </div>

          <div style={styles.sectionSubTitle}>Provider Matrix</div>
          {(status?.providers || []).length ? (
            (status?.providers || []).map((provider: any) => (
              <div key={provider.name} style={styles.providerCard}>
                <div style={styles.providerName}>{provider.name}</div>
                <div style={styles.providerMeta}>
                  Configured: {provider.configured ? "Yes" : "No"} • Online:{" "}
                  {provider.online ? "Yes" : "No"} • Active:{" "}
                  {provider.active ? "Yes" : "No"} • Preferred:{" "}
                  {provider.preferred ? "Yes" : "No"}
                </div>
              </div>
            ))
          ) : (
            <div style={styles.note}>No provider matrix available yet.</div>
          )}
        </AppSectionCard>

          <AppSectionCard>
            <div style={{ ...styles.sectionTitle, color: "#9ffcff" }}>
              Live User Activity
            </div>

            <div style={{ ...styles.note, color: "#e8fbff", marginBottom: "12px" }}>
              Real-time admin visibility for recent platform activity.
            </div>

            {userActivity ? (
              <div
                style={{
                  border: "1px solid rgba(120, 245, 255, 0.35)",
                  background: "rgba(0, 255, 255, 0.06)",
                  borderRadius: "16px",
                  padding: "14px",
                  color: "#f4ffff",
                  fontSize: "13px",
                  lineHeight: 1.6,
                  maxHeight: "320px",
                  overflow: "auto",
                  boxShadow: "0 0 24px rgba(0, 255, 255, 0.10)",
                }}
              >
                <div style={{ display: "grid", gap: 16 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 12,
                }}
              >
                <div style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(0,255,255,0.08)",
                  border: "1px solid rgba(143,247,255,0.18)"
                }}>
                  <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Total Users</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: "#eaffff" }}>
                    {userActivity?.total_users ?? 0}
                  </div>
                </div>

                <div style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(0,255,180,0.08)",
                  border: "1px solid rgba(0,255,180,0.18)"
                }}>
                  <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Online Now</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: "#9ffcff" }}>
                    {userActivity?.online_now ?? 0}
                  </div>
                </div>

                <div style={{
                  padding: 14,
                  borderRadius: 16,
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.12)"
                }}>
                  <div style={{ fontSize: 12, opacity: 0.72, marginBottom: 6 }}>Status</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: userActivity?.ok ? "#9ffcff" : "#ff9f9f" }}>
                    {userActivity?.ok ? "Healthy" : "Needs Attention"}
                  </div>
                </div>
              </div>

              <div style={{
                padding: 16,
                borderRadius: 18,
                background: "rgba(2,8,18,0.58)",
                border: "1px solid rgba(143,247,255,0.14)"
              }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#8ff7ff", marginBottom: 12 }}>
                  Activity Timeline
                </div>

                <div style={{ display: "grid", gap: 9 }}>
                  {(userActivity?.timeline || []).map((item: any, idx: number) => {
                    const maxCount = Math.max(1, ...(userActivity?.timeline || []).map((x: any) => Number(x.count || 0)));
                    const width = Math.max(4, Math.round((Number(item.count || 0) / maxCount) * 100));
                    return (
                      <div key={`${item.hour}-${idx}`} style={{ display: "grid", gridTemplateColumns: "52px 1fr 34px", gap: 10, alignItems: "center" }}>
                        <div style={{ fontSize: 12, color: "rgba(234,255,255,0.68)" }}>{item.hour}</div>
                        <div style={{ height: 9, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                          <div style={{
                            height: "100%",
                            width: `${width}%`,
                            borderRadius: 999,
                            background: "linear-gradient(90deg, #00eaff, #8ff7ff)",
                            boxShadow: "0 0 14px rgba(0,255,255,0.35)"
                          }} />
                        </div>
                        <div style={{ fontSize: 12, color: "#eaffff", textAlign: "right", fontWeight: 800 }}>{item.count}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
              </div>
            ) : (
              <div
                style={{
                  border: "1px solid rgba(255,255,255,0.16)",
                  background: "rgba(255,255,255,0.05)",
                  borderRadius: "16px",
                  padding: "14px",
                  color: "#d9faff",
                  fontSize: "13px",
                  textAlign: "center",
                  minHeight: "150px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div style={{ fontSize: "32px", marginBottom: "8px", opacity: 0.85 }}>📈</div>
                <div style={{ fontWeight: 800, marginBottom: "4px" }}>No live activity loaded yet.</div>
                <div style={{ opacity: 0.82, marginBottom: "12px" }}>
                  Click Refresh after signing in as admin.
                </div>
                <button type="button" style={pageStyles.button} onClick={loadAll}>
                  Refresh Activity
                </button>
              </div>
            )}
          </AppSectionCard>

        <LocalLibrarianPanel
          isElevated={isElevated}
        />

        <AppSectionCard>
          <div style={styles.sectionTitle}>Pro Invitation Center</div>

          <input
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="user@email.com"
            style={styles.input}
          />

          <input
            value={inviteDays}
            onChange={(e) => setInviteDays(e.target.value)}
            placeholder="5"
            style={{ ...styles.input, marginTop: "10px" }}
          />

          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              marginTop: "12px",
            }}
          >
            <button
              type="button"
              style={pageStyles.button}
              onClick={() =>
                postAction("/api/v1/admin/send-invite", {
                  email: inviteEmail,
                  expires_days: Number(inviteDays || 5),
                  lifetime_vip: true,
                })
              }
            >
              Send Pro Invitation
            </button>

            <button
              type="button"
              style={pageStyles.button}
              onClick={() => {
                const cleanInviteEmail = String(inviteEmail || "").trim().toLowerCase();

                if (!cleanInviteEmail || !cleanInviteEmail.includes("@")) {
                  setActionMsg("❌ Enter or click a valid invite email first.");
                  return;
                }

                const ok = window.confirm(`Revoke Pro invitation?\n\nEmail: ${cleanInviteEmail}\n\nContinue?`);
                if (!ok) {
                  setActionMsg("Invite revoke cancelled.");
                  return;
                }

                postAction("/api/v1/admin/revoke-invite", {
                  email: cleanInviteEmail,
                });
              }}
            >
              Revoke Pro Invitation
            </button>

            <button
              type="button"
              style={{
                ...pageStyles.button,
                border: "1px solid rgba(255,140,140,0.6)",
                color: "#ffd0d0"
              }}
              onClick={async () => {
                if (!window.confirm("Clear old/revoked invite history?")) {
                  return;
                }

                await postAction(
                  "/api/v1/admin/clear-invite-history",
                  {}
                );

                await loadAll();
              }}
            >
              Clear History
            </button>

          </div>

          <div style={styles.sectionSubTitle}>Recent Invites</div>
          <div style={styles.tableWrap}>
            {(invites || []).length ? (
              (invites || []).slice(0, 8).map((invite: any) => (
                <div
                  key={invite.id}
                  style={{ ...styles.row, cursor: "pointer" }}
                  onClick={() => {
                    setInviteEmail(String(invite.email || "").trim().toLowerCase());
                    setActionMsg(`Selected invite: ${invite.email}`);
                  }}
                >
                  <div style={styles.rowMain}>
                    <div style={styles.rowTitle}>{invite.email}</div>
                    <div style={styles.rowMeta}>
                      {invite.status || "pending"} • expires: {invite.expires_at || "—"}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={styles.note}>No invites found yet.</div>
            )}
          </div>

          {invitesWarning ? <div style={styles.warnBox}>{invitesWarning}</div> : null}
        </AppSectionCard>

        
        <AppSectionCard>
          <div style={styles.sectionTitle}>User Actions</div>

          <input
            value={selectedEmail}
            onChange={(e) => setSelectedEmail(e.target.value)}
            placeholder="Select or type user email"
            style={styles.input}
          />

          <div style={styles.tableWrap}>
            {(users || []).length ? (
              (users || []).slice(0, 10).map((user: any) => (
                <div
                  key={user.email}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 10px",
                    marginBottom: "6px",
                    borderRadius: "10px",
                    background: selectedEmail === user.email
                      ? "rgba(0,255,255,0.08)"
                      : "rgba(255,255,255,0.03)",
                    cursor: "pointer"
                  }}
                  onClick={() => setSelectedEmail(user.email)}
                >
                  <span style={{ fontSize: "12px", color: "#ccc" }}>
                    {user.email} • {user.role} • {user.status}
                  </span>

                  {selectedEmail === user.email && (
                    <span
                      style={{
                        color: "#00ffff",
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                        marginLeft: "10px",
                        flexShrink: 0
                      }}
                    >
                      SELECTED
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div style={styles.note}>No users loaded yet.</div>
            )}
          </div>

          <div style={{ marginTop: "12px", color: "#aaa", fontSize: "12px" }}>
            Selected: {selectedEmail || "None"}
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              flexWrap: "wrap",
              marginTop: "12px",
            }}
          >

            <button style={pageStyles.button} onClick={() =>
              confirmUserAction("Grant Pro Access", "/api/v1/admin/grant-vip")
            }>Grant Pro Access</button>

            <button style={pageStyles.button} onClick={() =>
              confirmUserAction("Remove Admin-Granted Pro", "/api/v1/admin/revoke-vip")
            }>Remove Admin-Granted Pro</button>

            <button style={pageStyles.button} onClick={() =>
              confirmUserAction("Promote to Admin", "/api/v1/admin/promote-admin", true)
            }>Promote to Admin</button>


            <button style={pageStyles.button} onClick={() =>
              confirmUserAction("Reset to Free User", "/api/v1/admin/demote-admin", true)
            }>Reset to Free User</button>

            <button style={pageStyles.button} onClick={() =>
              confirmUserAction("Ban Account", "/api/v1/admin/ban", true)
            }>Ban Account</button>

            <button style={pageStyles.button} onClick={() =>
              confirmUserAction("Restore Account", "/api/v1/admin/unban")
            }>Restore Account</button>

            <button
              style={{
                ...pageStyles.button,
                border: "1px solid rgba(255,70,70,0.75)",
                color: "#ff9b9b"
              }}
              onClick={() => {
                if (!selectedEmail) {
                  setActionMsg("Select a user first.");
                  return;
                }
                const ok = window.confirm(`⚠️ Delete User Permanently\n\nUser: ${selectedEmail}\n\nThis cannot be undone. Continue?`);
                if (!ok) return;
                postAction("/api/v1/admin/delete-user", { email: selectedEmail }, "DELETE");
              }}
            >
              Delete User
            </button>

          </div>
        </AppSectionCard>

      </div>

        </div>

        {!isElevated ? (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "absolute",
              zIndex: 20,
              top: "34px",
              left: "50%",
              width: "min(520px, calc(100% - 32px))",
              transform: "translateX(-50%)",
              boxSizing: "border-box",
              borderRadius: "20px",
              border:
                "1px solid rgba(76, 201, 255, 0.66)",
              background:
                "rgba(5, 18, 33, 0.96)",
              boxShadow:
                "0 0 48px rgba(30, 168, 255, 0.3)",
              padding: "24px",
              textAlign: "center",
              backdropFilter: "blur(16px)",
            }}
          >
            <div
              style={{
                fontSize: "1.15rem",
                fontWeight: 900,
                letterSpacing: "0.05em",
              }}
            >
              ADMIN CONSOLE PROTECTED
            </div>

            <div
              style={{
                marginTop: "10px",
                lineHeight: 1.6,
                opacity: 0.82,
              }}
            >
              User records, system controls, invitations,
              activity, and platform operations remain hidden
              until Super Admin verification is complete.
            </div>

            <button
              type="button"
              style={{
                ...pageStyles.button,
                marginTop: "18px",
              }}
              disabled={
                elevationBusy ||
                elevation?.eligible === false
              }
              onClick={() => {
                setElevationMessage("");
                setElevationModalOpen(true);
              }}
            >
              Unlock Admin Console
            </button>
          </div>
        ) : null}
      </div>

    </AppPageShell>
  );
}

const styles: Record<string, React.CSSProperties> = {
  topRow: {
    display: "flex",
    gap: "14px",
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: "26px",
    padding: "14px",
    borderRadius: "20px",
    border: "1px solid rgba(103,232,249,0.14)",
    background:
      "linear-gradient(135deg, rgba(0,255,255,0.07), rgba(255,255,255,0.025))",
    boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
  },
  pill: {
    padding: "11px 16px",
    borderRadius: "999px",
    border: "1px solid rgba(103,232,249,0.45)",
    background: "linear-gradient(135deg, rgba(8,35,48,0.94), rgba(4,12,24,0.86))",
    color: "#bffcff",
    fontSize: "12px",
    fontWeight: 900,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    boxShadow: "inset 0 0 18px rgba(0,255,255,0.08)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
    gap: "18px",
    alignItems: "stretch",
  },
  sectionTitle: {
    fontSize: "18px",
    fontWeight: 950,
    color: "#f7ffff",
    letterSpacing: "0.055em",
    textTransform: "uppercase",
    marginBottom: "14px",
    textShadow: "0 0 18px rgba(103,232,249,0.18)",
  },
  sectionSubTitle: {
    fontSize: "13px",
    fontWeight: 950,
    color: "#67f7ff",
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    marginTop: "20px",
    marginBottom: "12px",
  },
  badgeRow: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    marginBottom: "12px",
  },
  bigBadge: {
    padding: "10px 14px",
    borderRadius: "14px",
    background: "linear-gradient(135deg, rgba(34,211,238,0.24), rgba(14,165,233,0.12))",
    border: "1px solid rgba(103,232,249,0.36)",
    color: "#ecfeff",
    fontWeight: 950,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    fontSize: "12px",
    boxShadow: "0 0 24px rgba(34,211,238,0.14)",
  },
  smallBadge: {
    padding: "10px 14px",
    borderRadius: "14px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.13)",
    color: "#ffffff",
    fontWeight: 850,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    fontSize: "12px",
  },
  statRow: {
    padding: "12px 0",
    borderBottom: "1px solid rgba(255,255,255,0.075)",
    display: "grid",
    gridTemplateColumns: "minmax(120px, 0.9fr) minmax(0, 1.1fr)",
    gap: "14px",
    alignItems: "center",
  },
  statLabel: {
    color: "#67f7ff",
    fontSize: "11px",
    fontWeight: 850,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    marginBottom: 0,
  },
  statValue: {
    color: "#ffffff",
    fontSize: "14px",
    lineHeight: 1.55,
    wordBreak: "break-word",
    textAlign: "right",
    fontWeight: 650,
  },
  inputLabel: {
    color: "#67e8f9",
    fontSize: "12px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    display: "block",
  },
  input: {
    width: "100%",
    minHeight: "50px",
    borderRadius: "14px",
    border: "1px solid rgba(103,232,249,0.16)",
    background: "rgba(3,10,20,0.86)",
    color: "#fff",
    padding: "0 15px",
    boxSizing: "border-box",
    outline: "none",
    fontSize: "14px",
    boxShadow: "inset 0 0 18px rgba(0,0,0,0.22)",
  },
  select: {
    width: "100%",
    minHeight: "50px",
    borderRadius: "14px",
    border: "1px solid rgba(103,232,249,0.16)",
    background: "rgba(3,10,20,0.86)",
    color: "#fff",
    padding: "0 15px",
    boxSizing: "border-box",
    outline: "none",
    fontSize: "14px",
    marginTop: "8px",
    boxShadow: "inset 0 0 18px rgba(0,0,0,0.22)",
  },
  tableWrap: {
    marginTop: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "9px",
    maxHeight: "285px",
    overflowY: "auto",
    paddingRight: "4px",
  },
  row: {
    padding: "12px 13px",
    borderRadius: "14px",
    border: "1px solid rgba(103,232,249,0.10)",
    background: "rgba(255,255,255,0.04)",
    boxShadow: "inset 0 0 16px rgba(255,255,255,0.015)",
  },
  rowMain: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  rowTitle: {
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 700,
  },
  rowMeta: {
    color: "rgba(255,255,255,0.70)",
    fontSize: "12px",
  },
  userPick: {
    textAlign: "left",
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.03)",
    color: "#fff",
    borderRadius: "12px",
    padding: "10px 12px",
    cursor: "pointer",
  },
  providerCard: {
    padding: "12px 0",
    borderBottom: "1px solid rgba(255,255,255,0.075)",
  },
  providerName: {
    color: "#ffffff",
    fontSize: "15px",
    fontWeight: 700,
    marginBottom: "6px",
  },
  providerMeta: {
    color: "rgba(255,255,255,0.78)",
    fontSize: "13px",
    lineHeight: 1.6,
  },
  note: {
    color: "rgba(235,252,255,0.82)",
    lineHeight: 1.7,
    fontSize: "14px",
    marginTop: "12px",
  },
  notice: {
    marginBottom: "16px",
    padding: "12px 14px",
    borderRadius: "14px",
    border: "1px solid rgba(103,232,249,0.18)",
    background: "rgba(34,211,238,0.06)",
    color: "#bff7ff",
  },
  error: {
    marginBottom: "16px",
    padding: "12px 14px",
    borderRadius: "14px",
    border: "1px solid rgba(255,120,120,0.25)",
    background: "rgba(255,80,80,0.08)",
    color: "#ffb3b3",
  },
  warnBox: {
    marginTop: "14px",
    padding: "11px 13px",
    borderRadius: "14px",
    border: "1px solid rgba(255,179,179,0.22)",
    background: "rgba(255,179,179,0.07)",
    color: "#ffb3b3",
    fontSize: "13px",
    lineHeight: 1.5,
  },
};
